import type { CdnProvider } from '@/lib/cdn/config';
import { getCdnBaseUrl } from '@/lib/cdn/config';

type CloudflareFit = 'contain' | 'cover' | 'crop' | 'pad' | 'scale-down';

interface cloudflareLoaderProps {
  aspectRatioHeight?: number;
  aspectRatioWidth?: number;
  /** 6-digit hex without leading '#', e.g. "FFFFFF" */
  background?: string;
  bypassTransform?: boolean;
  fit?: CloudflareFit;
  focalPoint?: { x: number; y: number };
  originalImageDimensions?: { x: number; y: number };
  requestWidth?: number;
  src: string;
  /** Automatically trim whitespace around the image */
  trim?: boolean;
  width?: number;
}

const DEFAULT_GRAVITY = 0.5;

const normalizeGravityAxis = (focal?: number, dimension?: number): number => {
  if (!focal || !dimension) return DEFAULT_GRAVITY;
  return Math.round(Math.min(1, Math.max(0, focal / dimension)) * 100) / 100;
};

const normalizeGravity = (
  focalPoint?: { x: number; y: number },
  originalImageDimensions?: { x: number; y: number },
): string => {
  const gx = normalizeGravityAxis(focalPoint?.x, originalImageDimensions?.x);
  const gy = normalizeGravityAxis(focalPoint?.y, originalImageDimensions?.y);
  return `gravity=${gx}x${gy}`;
};

export const cloudflareImageLoader = (
  {
    aspectRatioHeight,
    aspectRatioWidth,
    background,
    bypassTransform,
    fit = 'cover',
    focalPoint,
    originalImageDimensions,
    requestWidth,
    src,
    trim,
    width,
  }: cloudflareLoaderProps,
  provider?: CdnProvider,
) => {
  if (src && !src.startsWith('https')) return src;

  const baseUrl = getCdnBaseUrl(provider).replace(/\/+$/, '');

  if (bypassTransform) {
    return `${baseUrl}/cdn-cgi/image/format=webp,quality=100/api/image-proxy/${src.replace(
      'https://',
      '',
    )}`;
  }

  const maxNeededWidth = requestWidth ? requestWidth * 3 : undefined;
  const resolvedWidth =
    Number.isFinite(width) && (width as number) > 0 ? (width as number) : 1280;
  const finalWidth =
    maxNeededWidth && resolvedWidth > maxNeededWidth
      ? maxNeededWidth
      : resolvedWidth;

  const finalHeight =
    finalWidth && aspectRatioHeight && aspectRatioWidth
      ? Math.round(finalWidth * (aspectRatioHeight / aspectRatioWidth))
      : undefined;

  const gravity =
    fit === 'pad' && !focalPoint
      ? 'gravity=auto'
      : normalizeGravity(focalPoint, originalImageDimensions);
  const size = finalHeight
    ? `width=${finalWidth},height=${finalHeight}`
    : `width=${finalWidth}`;

  const bgOption = background ? `,background=%23${background}` : '';
  const trimOption = trim ? `,trim=border,trim.border.tolerance=30` : '';

  const url = new URL(
    `${baseUrl}/cdn-cgi/image/format=auto,quality=90,${size},fit=${fit},${gravity}${bgOption}${trimOption}/api/image-proxy/${src?.replace(
      'https://',
      '',
    )}`,
  );

  return url.href;
};

export const buildTrimmedImageUrl = (src: string, width = 1200): string => {
  if (!src || !src.startsWith('https')) return src;
  const baseUrl = getCdnBaseUrl('cloudflare').replace(/\/+$/, '');
  return `${baseUrl}/cdn-cgi/image/format=auto,quality=90,width=${width},fit=contain,trim=border,trim.border.tolerance=30/api/image-proxy/${src.replace(
    'https://',
    '',
  )}`;
};

export const cloudflareVideoLoader = (src: string) => {
  if (!src || !src.startsWith('https')) return src;
  const baseUrl = getCdnBaseUrl().replace(/\/+$/, '');
  return `${baseUrl}/api/image-proxy/${src.replace('https://', '')}`;
};
